import { IAPIErrorObject, IErrorParsing } from '../error-parsing';
import { IResponseBase } from '../api-client';


// disable type-checking as this service is explicitly dedicated to parsing objects of potentially unknown type
/* eslint-disable @typescript-eslint/no-explicit-any */
function isApiResponse(errorObject: any | IResponseBase): errorObject is IResponseBase {
    return errorObject && errorObject.response != null;
}

function isGraphqlResponse(extractFrom: any) {
    return extractFrom?.errors && Array.isArray(extractFrom?.errors);
}

class ErrorParsing extends IErrorParsing {
    private static readonly isRetryable = '__isRetryable';

    extractAPIError(extractFrom: any): IAPIErrorObject {
        if (isApiResponse(extractFrom)) {
            const errorObject: IAPIErrorObject = {
                errorCode: extractFrom.response.status,
                errorMessage: extractFrom.rawResponse as string,
            };
            try {
                const {fields, message, messages} = JSON.parse(extractFrom.rawResponse as string);

                if (fields?.length) {
                    errorObject.fields = fields;
                    errorObject.errorMessage = fields.map(error => error.message).join(' ');
                } else if (messages?.length) {
                    errorObject.errorMessage = messages.join('\r\n');
                } else if (message) {
                    errorObject.errorMessage = message;
                }
            } catch (e) {
                // do nothing, in case error message can not be extracted
            }

            return errorObject;
        } else {
            return null;
        }
    }

    extractGraphqlError(extractFrom: any) {
        let errorObject = extractFrom?.errors?.find(err => err?.errorType);
        errorObject = JSON.parse(errorObject?.message);
        return errorObject || null;
    }

    extractMessage(extractFrom: any): string {
        // try extract from api response
        if (isApiResponse(extractFrom)) {
            const errorObject = this.extractAPIError(extractFrom);
            return errorObject.errorMessage;
        } else if (isGraphqlResponse(extractFrom)) {
            // try extract from Graphql response
            const errorObject = this.extractGraphqlError(extractFrom);
            return errorObject?.message || null;
        } else if (extractFrom instanceof Error) {
            // try extract from error object
            return extractFrom.name + ': ' + extractFrom.message;
        } else {
            // assume string error otherwise
            return extractFrom.toString();
        }
    }

    is5xxError(extractFrom: any): boolean {
        const apiError = this.extractAPIError(extractFrom);
        return apiError?.errorCode >= 500 && apiError?.errorCode < 600;
    }

    isRetryableApiError(extractFrom: any): boolean {
        return this.is5xxError(extractFrom) && Reflect.get(extractFrom, ErrorParsing.isRetryable) === true;
    }

    setAsRetryableApiError(extractFrom: any): void {
        if (this.is5xxError(extractFrom)) {
            Reflect.set(extractFrom, ErrorParsing.isRetryable, true);
        }
    }

    unsetRetryableApiError(extractFrom: any): any {
        if (this.isRetryableApiError(extractFrom)) {
            Reflect.deleteProperty(extractFrom, ErrorParsing.isRetryable);
        }
    }
}

export { ErrorParsing };
